import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    horizontalListSortingStrategy,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
dayjs.extend(customParseFormat);
import { useToast } from '../../hooks/useToast';
import { usePermissions } from '../../hooks/usePermissions';
import { useDataScope } from '../../hooks/useDataScope';
import { getReservationsByDateRange, updateReservation, getUniqueValues, getWaitingReservations } from '../../services/business/reservationService';
import { getDocks } from '../../services/business/dockService';
import { getGeneralSettings } from '../../services/firestore/systemConfigService';
import ReservationModal from '../../components/business/ReservationModal';
import Button from '../../components/shared/Button';
import Combobox from '../../components/shared/Combobox';
import { getAllTransporters } from '../../services/firestore/transporterService';
import { getVehicleTypes } from '../../services/business/vehicleTypeService';
import { getConfigsByCategory } from '../../services/firestore/systemConfigService';
import { CONFIG_CATEGORIES } from '../../constants/systemConfig';
import { VEHICLE_STATUS, STATUS_LABELS, STATUS_COLORS } from '../../constants/statuses';
import { isTransporterMatch, buildTransporterIdentities, buildUserIdentities } from '../../services/business/transporterMatchingService';

// Sortable Header Component
const SortableHeader = ({ column, sortConfig, onSort }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id: column.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        cursor: 'pointer', // Changed to pointer for click
        zIndex: isDragging ? 999 : 'auto',
        position: 'relative',
        background: isDragging ? 'var(--bg-secondary)' : 'transparent',
    };

    if (!column.visible) return null;

    const handleSort = (e) => {
        // Prevent sorting if dragging
        if (isDragging) return;
        onSort(column.id);
    };

    return (
        <th
            ref={setNodeRef}
            style={{ ...style, color: 'var(--text-primary)', backgroundColor: 'var(--bg-secondary)' }}
            className="px-4 py-3 text-xs font-bold text-left whitespace-nowrap border-b border-r border-white/10 select-none hover:bg-white/5 transition-colors cursor-pointer"
            onClick={() => onSort(column.id)}
        >
            <div className="flex items-center gap-xs" {...listeners} {...attributes}>
                <span>{column.label}</span>
                {sortConfig?.key === column.id && (
                    <span className="text-xs text-primary">
                        {sortConfig.direction === 'asc' ? ' ▲' : ' ▼'}
                    </span>
                )}
                {!sortConfig?.key || sortConfig.key !== column.id ? (
                    <span className="opacity-30 text-xs"> ⇅</span>
                ) : null}
            </div>
        </th>
    );
};

const ReservationTracking = ({ userData }) => {
    const { user } = usePermissions(userData);
    const scope = useDataScope(userData);
    const { success, error: toastError } = useToast();
    const [loading, setLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [reservations, setReservations] = useState([]);
    const [docks, setDocks] = useState([]);
    const [allTransporters, setAllTransporters] = useState([]);
    const [vehicleTypes, setVehicleTypes] = useState([]);
    const [transportModes, setTransportModes] = useState([]);
    const [transporterMap, setTransporterMap] = useState({});
    const columnMenuTriggerRef = useRef(null);
    const [isColumnMenuOpen, setIsColumnMenuOpen] = useState(false);
    const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });

    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [searchParams] = useSearchParams();

    useEffect(() => {
        if (isColumnMenuOpen && columnMenuTriggerRef.current) {
            const rect = columnMenuTriggerRef.current.getBoundingClientRect();
            setMenuPosition({
                top: rect.bottom + 8,
                right: window.innerWidth - rect.right
            });
        }
    }, [isColumnMenuOpen]);

    // Modal State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedReservation, setSelectedReservation] = useState(null);

    // Initial Columns (Fixed: Removed 'date' filter from here as it's now global)
    const initialColumns = [
        { id: 'docNo', label: 'Belge No', visible: true, type: 'text' },
        { id: 'transporter', label: 'Firma', visible: true, type: 'text' },
        { id: 'plate', label: 'Plaka', visible: true, type: 'text' },
        { id: 'vehicleType', label: 'A. Tipi', visible: true, type: 'text' },
        { id: 'transportType', label: 'Nak. Türü', visible: true, type: 'text' },
        { id: 'status', label: 'Durum', visible: true, type: 'select' },
        { id: 'delayed', label: 'Gecikme', visible: true, type: 'text' },
        { id: 'volume', label: 'Hacim', visible: true, type: 'text' },
        { id: 'weight', label: 'Ağırlık', visible: true, type: 'text' },
        { id: 'date', label: 'Tarih', visible: true, type: 'date' },
        { id: 'time', label: 'Saat', visible: true, type: 'time' },
        { id: 'dock', label: 'Rampa', visible: true, type: 'select' },
        { id: 'creator', label: 'Oluşturan', visible: true, type: 'text' },
    ];

    const [columns, setColumns] = useState(initialColumns);
    const [weightUnit, setWeightUnit] = useState('kg');
    const [volumeUnit, setVolumeUnit] = useState('m³');

    // Sort Config State
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    const [suggestions, setSuggestions] = useState({
        docNos: [],
        transporters: [],
        plates: []
    });

    // Filters State
    const [dateRange, setDateRange] = useState({
        start: dayjs().format('YYYY-MM-DD'),
        end: dayjs().format('YYYY-MM-DD')
    });

    const [filters, setFilters] = useState({
        global: '',
        docNo: '',
        transporter: '',
        plate: '',
        vehicleType: '',
        transportType: '',
        status: '',
        dock: '',
    });

    // Sensors for Drag and Drop
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    useEffect(() => {
        if (user?.tenantId && userData) {
            loadData();
        }
    }, [user?.tenantId, userData?.id, userData?.role, userData?.transporterId, userData?.transporterName]);

    // Re-load when date range changes
    useEffect(() => {
        if (user?.tenantId) {
            loadData();
        }
    }, [dateRange.start, dateRange.end]);

    // URL Parameters Handler
    useEffect(() => {
        if (!userData || !allTransporters.length) return;

        const urlTransporter = searchParams.get('transporter');
        const urlStatus = searchParams.get('status');
        const urlStart = searchParams.get('startDate');
        const urlEnd = searchParams.get('endDate');

        if (urlTransporter || urlStatus || urlStart || urlEnd) {
            setFilters(prev => ({
                ...prev,
                transporter: urlTransporter || prev.transporter,
                status: urlStatus || prev.status
            }));

            if (urlStart || urlEnd) {
                setDateRange({
                    start: urlStart || dateRange.start,
                    end: urlEnd || dateRange.end
                });
            }

            setHasSearched(true);
            loadData();
        }
    }, [searchParams, allTransporters.length, !!userData]);

    // PRE-FILL TRANSPORTER FILTER (Ensures the dropdown shows their name if they have a matching record)
    useEffect(() => {
        if (userData?.role === 'transporter' && allTransporters.length > 0) {
            const myId = userData.transporterId;
            const match = allTransporters.find(t => t.id === myId || t.supplierCode === userData.supplierCode);
            if (match && !filters.transporter) {
                setFilters(prev => ({ ...prev, transporter: match.id }));
            }
        }
    }, [userData, allTransporters]);



    const loadData = async () => {
        setLoading(true);
        setHasSearched(true);
        if (!user?.tenantId) {
            toastError('Kullanıcı bilgisi bulunamadı.');
            setLoading(false);
            return;
        }
        try {
            const [resData, dockData, tData, waitingData, vTypes, tModes, settings] = await Promise.all([
                getReservationsByDateRange(user.tenantId, dateRange.start, dateRange.end),
                getDocks(user.tenantId),
                getAllTransporters(user.tenantId),
                getWaitingReservations(user.tenantId),
                getVehicleTypes(user.tenantId),
                getConfigsByCategory(user.tenantId, CONFIG_CATEGORIES.TRANSPORT_MODES),
                getGeneralSettings(user.tenantId)
            ]);

            if (settings) {
                if (settings.weightUnit) setWeightUnit(settings.weightUnit);
                if (settings.volumeUnit) {
                    setVolumeUnit(settings.volumeUnit === 'M3' ? 'm³' : settings.volumeUnit);
                }
            }

            // Combine both scheduled and waiting reservations
            const combinedData = [...(resData || []), ...(waitingData || [])];

            // Avoid duplicates (in case a waiting one technically matches date range but has no dock)
            const uniqueData = Array.from(new Map(combinedData.map(item => [item.id, item])).values());

            // --- DATA SCOPE FILTERING ---
            let filteredReservations = uniqueData;

            // Transporter specific restriction - Robust Identity Matching
            if (userData?.role === 'transporter') {
                // --- Robust Identity Matching using Shared Service ---
                const myIdentities = buildUserIdentities(userData);

                // Enrich from master transporter list
                tData?.forEach(t => {
                    const tIdentities = buildTransporterIdentities(t);
                    const isMatch = Array.from(tIdentities).some(tid => myIdentities.has(tid));

                    if (isMatch) {
                        tIdentities.forEach(id => myIdentities.add(id));
                    }
                });

                filteredReservations = uniqueData.filter(r => {
                    // Shared matching logic
                    if (isTransporterMatch(r, myIdentities)) return true;

                    // Creator Match (Safety Fallback)
                    if (r.createdBy === userData?.uid || r.createdBy === userData?.id || r.createdById === userData?.uid) return true;

                    return false;
                });
            }

            let filteredDocks = dockData;

            // --- SCOPE FILTERING (Skip for transporters to avoid accidental exclusion) ---
            if (!scope.isFullAccess && userData?.role !== 'transporter') {
                // Filter Reservations by Factory/Dock Scope if defined
                if (scope.factoryIds && scope.factoryIds.length > 0) {
                    filteredReservations = filteredReservations.filter(r => scope.factoryIds.includes(r.factoryId));
                }

                if (scope.dockIds && scope.dockIds.length > 0) {
                    filteredReservations = filteredReservations.filter(r => scope.dockIds.includes(r.dockId));
                }

                // Filter Docks by Scope
                filteredDocks = dockData.filter(d => {
                    if (scope.dockIds && scope.dockIds.length > 0 && !scope.dockIds.includes(d.id)) return false;
                    if (scope.doorGroupIds && scope.doorGroupIds.length > 0 && !scope.doorGroupIds.includes(d.doorGroupId)) return false;
                    if (scope.factoryIds && scope.factoryIds.length > 0 && !scope.factoryIds.includes(d.factoryId)) return false;
                    return true;
                });
            }

            setReservations(filteredReservations);
            setDocks(filteredDocks);
            setAllTransporters(tData);
            setVehicleTypes(vTypes);
            setTransportModes(tModes);

            // Generate suggestions for filters
            setSuggestions({
                docNos: Array.from(new Set(uniqueData.map(r => r.reservationNumber || r.id || r.docNo || r.documentNumber).filter(v => v))),
                transporters: Array.from(new Set(uniqueData.map(r => r.transporterName || r.transporter).filter(v => v))),
                plates: Array.from(new Set(uniqueData.map(r => r.plateNumber).filter(v => v)))
            });

            // Map transporters for easy lookup
            const tMap = {};
            tData.forEach(t => {
                if (t.id) {
                    tMap[t.id] = t;
                    tMap[t.id.toLowerCase()] = t;
                }
                if (t.companyName) {
                    tMap[t.companyName] = t;
                    tMap[t.companyName.toLowerCase().trim()] = t;
                }
                if (t.shortName) {
                    tMap[t.shortName] = t;
                    tMap[t.shortName.toLowerCase().trim()] = t;
                }
            });
            setTransporterMap(tMap);
        } catch (err) {
            console.error(err);
            toastError('Veriler yüklenirken hata oluştu.');
        } finally {
            setLoading(false);
        }
    };

    // --- Drag & Drop Handler ---
    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (active.id !== over.id) {
            setColumns((items) => {
                const oldIndex = items.findIndex((i) => i.id === active.id);
                const newIndex = items.findIndex((i) => i.id === over.id);
                return arrayMove(items, oldIndex, newIndex);
            });
        }
    };

    const transporterOptions = useMemo(() => {
        let list = allTransporters;
        if (userData?.role === 'transporter') {
            const myIdentities = buildUserIdentities(userData);
            list = allTransporters.filter(t => {
                const tIdentities = buildTransporterIdentities(t);
                return Array.from(tIdentities).some(id => myIdentities.has(id));
            });
        }

        return list.map(t => ({
            id: t.id,
            label: `${t.supplierCode ? '[' + t.supplierCode + '] ' : ''}${t.companyName || t.shortName || 'İsimsiz Nakliyeci'}`,
            value: t.id
        }));
    }, [allTransporters, userData]);

    // --- Helper for Multi-Value Filtering ---
    const checkMultiMatch = (filterVal, rowVal) => {
        if (!filterVal) return true;
        const searchTerms = filterVal.split(',').map(t => t.trim().toLowerCase()).filter(t => t);
        if (searchTerms.length === 0) return true;

        const target = rowVal?.toLowerCase() || '';
        return searchTerms.some(term => target.includes(term));
    };

    const filteredData = useMemo(() => {
        let data = reservations.filter(row => {
            // Helper to get display name for vehicle type
            const vTypeName = row.vehicleTypeName ||
                row.metrics?.vehicleTypeName ||
                vehicleTypes.find(vt => vt.id === (row.vehicleTypeId || row.metrics?.vehicleType || row.vehicleType))?.name ||
                '';

            // Global Search
            const searchStr = filters.global.toLowerCase();
            const matchesGlobal =
                !searchStr ||
                (row.id || row.docNo || row.documentNumber || row.reservationNumber || '').toLowerCase().includes(searchStr) ||
                (row.plateNumber || '').toLowerCase().includes(searchStr) ||
                (row.transporterName || '').toLowerCase().includes(searchStr) ||
                vTypeName.toLowerCase().includes(searchStr);

            if (!matchesGlobal) return false;

            // Specific Filters (Multi-Value Supported)
            if (!checkMultiMatch(filters.docNo, row.reservationNumber || row.documentNumber || row.docNo || row.id)) return false;
            if (!checkMultiMatch(filters.plate, row.plateNumber)) return false;
            if (!checkMultiMatch(filters.vehicleType, vTypeName)) return false;

            // Transport Type Match
            const rTransportType = (row.transportType || row.metrics?.transportType || '').toLowerCase();
            if (!checkMultiMatch(filters.transportType, rTransportType)) return false;

            // Transporter Identity Filter (Only apply for non-transporters; transporters already have scoped data in reservations state)
            if (filters.transporter && userData?.role !== 'transporter') {
                const search = filters.transporter.toLowerCase();

                // Get identities for the selected transporter from map
                const selectedTransporter = transporterMap[filters.transporter] || transporterMap[filters.transporter.toLowerCase()];
                let searchIdentities;

                if (selectedTransporter) {
                    searchIdentities = buildTransporterIdentities(selectedTransporter);
                } else if (userData?.role === 'transporter' && (filters.transporter === userData.transporterId || filters.transporter === userData.supplierCode)) {
                    // Fallback: If we are a transporter and filtering by our own ID, use our own full identities
                    searchIdentities = buildUserIdentities(userData);
                } else {
                    searchIdentities = new Set([search]);
                }

                if (!isTransporterMatch(row, searchIdentities)) return false;
            }

            if (filters.status && row.status !== filters.status) return false;
            if (filters.dock && row.dockId !== filters.dock) return false;

            return true;
        });

        // Sorting
        if (sortConfig.key) {
            data.sort((a, b) => {
                let aValue, bValue;

                // Map header ID to data key
                switch (sortConfig.key) {
                    case 'time':
                        // Start Time comparison
                        aValue = a.startTime instanceof Date ? a.startTime : new Date(a.startTime || 0);
                        bValue = b.startTime instanceof Date ? b.startTime : new Date(b.startTime || 0);
                        break;
                    case 'docNo':
                        aValue = a.reservationNumber || a.documentNumber || a.docNo || a.id || '';
                        bValue = b.reservationNumber || b.documentNumber || b.docNo || b.id || '';
                        return sortConfig.direction === 'asc'
                            ? aValue.localeCompare(bValue, undefined, { numeric: true, sensitivity: 'base' })
                            : bValue.localeCompare(aValue, undefined, { numeric: true, sensitivity: 'base' });
                    case 'transporter':
                        aValue = a.transporterName || '';
                        bValue = b.transporterName || '';
                        break;
                    case 'plate':
                        aValue = a.plateNumber || '';
                        bValue = b.plateNumber || '';
                        break;
                    case 'status':
                        aValue = a.status || '';
                        bValue = b.status || '';
                        break;
                    case 'delayed':
                        {
                            const getDelayed = (row) => {
                                const planned = row.startTime instanceof Date ? row.startTime : new Date(row.startTime);
                                const actual = row.entryTime instanceof Date ? row.entryTime : (row.entryTime ? new Date(row.entryTime) : null);
                                const now = new Date();
                                if (actual) return (actual - planned) / 60000 > 15;
                                if (!['cancelled', 'rejected', 'completed', 'facility_exited'].includes(row.status)) return (now - planned) / 60000 > 15;
                                return false;
                            };
                            aValue = getDelayed(a) ? 1 : 0;
                            bValue = getDelayed(b) ? 1 : 0;
                        }
                        break;
                    case 'dock':
                        const dockA = docks.find(d => d.id === a.dockId)?.name || '';
                        const dockB = docks.find(d => d.id === b.dockId)?.name || '';
                        aValue = dockA;
                        bValue = dockB;
                        break;
                    case 'volume':
                        aValue = Number(a.metrics?.volume || a.plannedVolume || 0);
                        bValue = Number(b.metrics?.volume || b.plannedVolume || 0);
                        break;
                    case 'weight':
                        aValue = Number(a.metrics?.weight || a.plannedWeight || 0);
                        bValue = Number(b.metrics?.weight || b.plannedWeight || 0);
                        break;
                    default:
                        // Try to find property matching the key
                        aValue = a[sortConfig.key] || '';
                        bValue = b[sortConfig.key] || '';
                }

                if (aValue < bValue) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }

        return data;
    }, [reservations, filters, sortConfig, docks, userData?.role, userData?.transporterId, allTransporters.length, transporterMap]);

    // --- Handlers ---
    const handleEdit = (res) => {
        const start = new Date(`${res.bookingDate}T${res.startTime}`); // Assuming string format again or adapting
        // If startTime is date object, handle it
        // The service returns Date objects for startTime/endTime fields, but 'bookingDate' is usually just YYYY-MM-DD string
        // Let's ensure robust handling:
        let startDateObj = res.startTime instanceof Date ? res.startTime : new Date(res.startTime);
        let endDateObj = res.endTime instanceof Date ? res.endTime : new Date(res.endTime);

        setSelectedReservation({
            ...res,
            startTime: startDateObj,
            endTime: endDateObj,
            transporterId: res.transporterId
        });
        setIsModalOpen(true);
    };

    const handleSaveReservation = async (formData) => {
        try {
            const dateStr = formData.bookingDate || selectedReservation.bookingDate || dayjs(selectedReservation.startTime).format('YYYY-MM-DD');
            const [hours, minutes] = formData.startTime.split(':').map(Number);

            const start = dayjs(dateStr).hour(hours).minute(minutes).second(0).toDate();
            const end = new Date(start.getTime() + formData.duration * 60000);

            const updatePayload = {
                ...formData,
                startTime: start,
                endTime: end
            };

            delete updatePayload.duration;
            delete updatePayload.bookingDate;

            await updateReservation(selectedReservation.id, updatePayload, userData, userData.tenantId);

            success('Rezervasyon güncellendi');
            setIsModalOpen(false);
            loadData();
        } catch (error) {
            console.error(error);
            toastError('Güncelleme başarısız');
        }
    };

    const toggleColumn = (id) => {
        setColumns(cols => cols.map(c => c.id === id ? { ...c, visible: !c.visible } : c));
    };

    const handleExportExcel = () => {
        if (!filteredData || filteredData.length === 0) {
            toastError('İndirilecek veri yok.');
            return;
        }

        // Prepare headers
        const visibleColumns = columns.filter(c => c.visible);
        const headers = visibleColumns.map(c => c.label).join(';');

        // Prepare rows
        const rows = filteredData.map(row => {
            return visibleColumns.map(col => {
                let val = '';
                switch (col.id) {
                    case 'docNo': val = row.reservationNumber || row.documentNumber || row.docNo || row.id || ''; break;
                    case 'transporter':
                        const t = (transporterMap[row.transporterId] || transporterMap[row.transporterName]);
                        const tName = t?.companyName || row.transporterName || '';
                        const tCode = t?.supplierCode || row.supplierCode || '';
                        val = tCode ? `[${tCode}] ${tName}` : tName;
                        break;
                    case 'plate': val = row.plateNumber || ''; break;
                    case 'vehicleType': val = row.vehicleType || ''; break;
                    case 'transportType': val = row.transportType || ''; break;
                    case 'status':
                        val = STATUS_LABELS[row.status] || row.status || '';
                        break;
                    case 'delayed':
                        {
                            const planned = row.startTime instanceof Date ? row.startTime : new Date(row.startTime);
                            const actual = row.entryTime instanceof Date ? row.entryTime : (row.entryTime ? new Date(row.entryTime) : null);
                            const now = new Date();
                            let isDelayed = false;
                            if (actual) {
                                isDelayed = (actual - planned) / 60000 > 15;
                            } else if (!['cancelled', 'rejected', 'completed', 'facility_exited'].includes(row.status)) {
                                isDelayed = (now - planned) / 60000 > 15;
                            }
                            val = isDelayed ? 'Gecikti' : '-';
                        }
                        break;
                    case 'volume': val = Number(row.metrics?.volume || row.plannedVolume || 0) + ' ' + volumeUnit; break;
                    case 'weight':
                        const weightVal = Number(row.metrics?.weight || row.plannedWeight || 0);
                        val = weightVal + ' ' + weightUnit;
                        break;
                    case 'date': val = row.startTime ? dayjs(row.startTime).format('DD.MM.YYYY') : (row.bookingDate || row.date || ''); break;
                    case 'time':
                        val = `${dayjs(row.startTime).format('HH:mm')} - ${dayjs(row.endTime).format('HH:mm')}`;
                        break;
                    case 'dock':
                        const d = docks.find(x => x.id === row.dockId);
                        val = d ? d.name : 'Atanmadı';
                        break;
                    case 'creator':
                        val = row.creatorName || (row.createdBy === 'system' ? 'Sistem' : row.createdBy) || '-';
                        break;
                    default: val = row[col.id] || '';
                }
                return `"${val}"`;
            }).join(';');
        }).join('\n');

        const csvContent = "\uFEFF" + headers + '\n' + rows; // Add BOM for Excel
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `rezervasyon_listesi_${dayjs().format('YYYY-MM-DD')}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // --- Totals Calculation ---
    const totals = useMemo(() => {
        return filteredData.reduce((acc, row) => {
            acc.volume += Number(row.metrics?.volume || row.plannedVolume || 0);
            acc.weight += Number(row.metrics?.weight || row.plannedWeight || 0);
            return acc;
        }, { volume: 0, weight: 0 });
    }, [filteredData]);

    // --- Render ---
    return (
        <div className="container pt-lg h-screen flex flex-col overflow-hidden">
            {/* Header Area */}
            <div className="flex justify-between items-center mb-md shrink-0 relative z-[1000]">
                <div>
                    <h1 className="text-2xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">Rezervasyon Takip</h1>
                    <p className="text-secondary text-sm">Operasyonel raporlama ve yönetim ekranı.</p>
                </div>
                <div className="flex gap-md items-center">
                    <Button variant="secondary" onClick={handleExportExcel}>
                        📊 Excel
                    </Button>
                    <Button variant="secondary" onClick={() => setIsFilterOpen(!isFilterOpen)}>
                        {isFilterOpen ? 'Filtreleri Gizle' : 'Filtre Göster'}
                    </Button>
                    <div className="relative" ref={columnMenuTriggerRef}>
                        <Button variant="secondary" onClick={() => setIsColumnMenuOpen(!isColumnMenuOpen)}>
                            Sütunlar ⚙️
                        </Button>
                        {isColumnMenuOpen && createPortal(
                            <div
                                className="fixed inset-0 z-[9999999]"
                                onClick={() => setIsColumnMenuOpen(false)}
                            >
                                <div
                                    className="absolute bg-[#0f172a] border border-slate-700 rounded-xl p-md shadow-[0_20px_50px_rgba(0,0,0,1)] w-64 translate-y-1 animate-in fade-in slide-in-from-top-2 duration-200 text-slate-200"
                                    style={{
                                        top: `${menuPosition.top}px`,
                                        right: `${menuPosition.right}px`,
                                        background: '#0f172a'
                                    }}
                                    onClick={e => e.stopPropagation()}
                                >
                                    <div className="flex items-center justify-between mb-md border-b border-white/10 pb-2">
                                        <h4 className="text-xs font-black uppercase tracking-widest font-mono" style={{ color: '#94a3b8' }}>Görünür Sütunlar</h4>
                                        <button
                                            onClick={() => setIsColumnMenuOpen(false)}
                                            className="transition-colors p-1"
                                            style={{ color: '#94a3b8' }}
                                            onMouseOver={e => e.currentTarget.style.color = 'white'}
                                            onMouseOut={e => e.currentTarget.style.color = '#94a3b8'}
                                        >
                                            ✕
                                        </button>
                                    </div>
                                    <div className="flex flex-col gap-sm max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
                                        {columns.map(col => (
                                            <label key={col.id} className="flex items-center gap-md cursor-pointer hover:bg-white/10 p-2 rounded-lg transition-all group/item">
                                                <input
                                                    type="checkbox"
                                                    className="w-4 h-4 rounded border-white/20 bg-white/10 text-primary focus:ring-primary transition-all cursor-pointer"
                                                    checked={col.visible}
                                                    onChange={() => toggleColumn(col.id)}
                                                />
                                                <span className="text-sm font-medium transition-colors" style={{ color: '#e2e8f0' }}>{col.label}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>,
                            document.body
                        )}
                    </div>
                </div>
            </div>

            {/* Filter Panel (Collapsible) */}
            {isFilterOpen && (
                <div className="card-premium glow-theme p-lg mb-md shrink-0 shadow-lg">
                    <div className="grid grid-cols-4 gap-md items-end">
                        {/* Date Range - Special Filter */}
                        <div className="flex flex-col gap-xs">
                            <label className="text-xs font-bold text-secondary">Tarih Aralığı</label>
                            <div className="flex gap-sm items-center">
                                <input
                                    type="date"
                                    className="input text-sm p-2 w-full"
                                    style={{ borderRadius: '8px', backgroundColor: 'rgba(255, 255, 255, 0.05)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
                                    value={dateRange.start}
                                    onChange={e => setDateRange({ ...dateRange, start: e.target.value })}
                                />
                                <span className="text-secondary">-</span>
                                <input
                                    type="date"
                                    className="input text-sm p-2 w-full"
                                    style={{ borderRadius: '8px', backgroundColor: 'rgba(255, 255, 255, 0.05)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
                                    value={dateRange.end}
                                    onChange={e => setDateRange({ ...dateRange, end: e.target.value })}
                                />
                            </div>
                        </div>

                        {/* Common Filters - Now Multi-Input Aware */}
                        <div className="flex flex-col gap-xs">
                            <label className="text-xs font-bold text-secondary">Nakliyeci</label>
                            <Combobox
                                items={transporterOptions}
                                value={filters.transporter}
                                onInputChange={(val) => setFilters({ ...filters, transporter: val })}
                                onChange={(item) => setFilters({ ...filters, transporter: item.id })}
                                placeholder="Nakliyeci seçin..."
                                labelKey="label"
                                valueKey="id"
                                inputStyle={{ borderRadius: '8px', backgroundColor: 'rgba(255, 255, 255, 0.05)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', colorScheme: 'dark' }}
                                disabled={userData?.role === 'transporter'}
                            />
                        </div>

                        <div className="flex flex-col gap-xs">
                            <label className="text-xs font-bold text-secondary">Belge No</label>
                            <input
                                className="input text-sm p-2 w-full"
                                style={{ borderRadius: '8px', backgroundColor: 'rgba(255, 255, 255, 0.05)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', colorScheme: 'dark' }}
                                placeholder="Örn: 1001, 1002..."
                                value={filters.docNo}
                                onChange={e => setFilters({ ...filters, docNo: e.target.value })}
                            />
                        </div>

                        <div className="flex flex-col gap-xs">
                            <label className="text-xs font-bold text-secondary">Plaka</label>
                            <input
                                className="input text-sm p-2 w-full"
                                style={{ borderRadius: '8px', backgroundColor: 'rgba(255, 255, 255, 0.05)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', colorScheme: 'dark' }}
                                placeholder="Örn: 34ABC12..."
                                value={filters.plate}
                                onChange={e => setFilters({ ...filters, plate: e.target.value })}
                            />
                        </div>

                        <div className="flex flex-col gap-xs">
                            <label className="text-xs font-bold text-secondary">Araç Tipi</label>
                            <Combobox
                                items={[
                                    ...vehicleTypes.map(v => ({ id: v.name, label: v.name }))
                                ]}
                                value={filters.vehicleType}
                                onInputChange={(val) => setFilters({ ...filters, vehicleType: val })}
                                onChange={(item) => setFilters({ ...filters, vehicleType: item.label })}
                                placeholder="Örn: Tır..."
                                labelKey="label"
                                valueKey="id"
                                inputStyle={{ borderRadius: '8px', backgroundColor: 'rgba(255, 255, 255, 0.05)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
                                disableFiltering={true}
                            />
                        </div>

                        <div className="flex flex-col gap-xs">
                            <label className="text-xs font-bold text-secondary">Nakliye Türü</label>
                            <Combobox
                                items={[
                                    ...transportModes.map(m => ({ id: m.name, label: m.name }))
                                ]}
                                value={filters.transportType}
                                onInputChange={(val) => setFilters({ ...filters, transportType: val })}
                                onChange={(item) => setFilters({ ...filters, transportType: item.label })}
                                placeholder="Örn: İthalat..."
                                labelKey="label"
                                valueKey="id"
                                inputStyle={{ borderRadius: '8px', backgroundColor: 'rgba(255, 255, 255, 0.05)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
                                disableFiltering={true}
                            />
                        </div>

                        <div className="flex flex-col gap-xs">
                            <label className="text-xs font-bold text-secondary">Statü</label>
                            <Combobox
                                items={[
                                    { id: '', label: 'Tümü' },
                                    ...Object.values(VEHICLE_STATUS).map(s => ({
                                        id: s,
                                        label: STATUS_LABELS[s] || s
                                    }))
                                ]}
                                value={[
                                    { id: '', label: 'Tümü' },
                                    ...Object.values(VEHICLE_STATUS).map(s => ({
                                        id: s,
                                        label: STATUS_LABELS[s] || s
                                    }))
                                ].find(o => o.id === filters.status)?.label || ''}
                                onChange={(item) => setFilters({ ...filters, status: item.id })}
                                placeholder="Seçiniz..."
                                labelKey="label"
                                valueKey="id"
                                inputStyle={{ borderRadius: '8px', backgroundColor: 'rgba(255, 255, 255, 0.05)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', colorScheme: 'dark' }}
                                disableFiltering={true}
                            />
                        </div>

                        <div className="flex flex-col gap-xs">
                            <label className="text-xs font-bold text-secondary">Rampa</label>
                            <Combobox
                                items={[
                                    { id: '', label: 'Tümü' },
                                    ...docks.map(d => ({ id: d.id, label: d.name }))
                                ]}
                                value={[
                                    { id: '', label: 'Tümü' },
                                    ...docks.map(d => ({ id: d.id, label: d.name }))
                                ].find(o => o.id === filters.dock)?.label || ''}
                                onChange={(item) => setFilters({ ...filters, dock: item.id })}
                                placeholder="Rampa Seçiniz..."
                                labelKey="label"
                                valueKey="id"
                                inputStyle={{ borderRadius: '8px', backgroundColor: 'rgba(255, 255, 255, 0.05)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', colorScheme: 'dark' }}
                                disableFiltering={true}
                            />
                        </div>



                        <div className="flex gap-sm items-end mb-1">
                            <Button
                                size="md"
                                variant="primary"
                                onClick={loadData}
                                style={{ borderRadius: '8px', minWidth: '100px', backgroundColor: 'var(--primary-color)', color: 'white', border: 'none' }}
                            >
                                🔍 ARA
                            </Button>

                            <Button
                                size="md"
                                onClick={() => setFilters({
                                    global: '', docNo: '', transporter: userData?.role === 'transporter' ? (userData.transporterId || userData.scope?.transporterId || '') : '', plate: '', status: '', dock: ''
                                })}
                                style={{
                                    background: 'transparent',
                                    border: '1px solid var(--border-color)',
                                    color: 'var(--text-secondary)',
                                    borderRadius: '8px'
                                }}
                            >
                                Temizle
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Table Container */}
            <div className="card-premium glow-theme flex-1 overflow-hidden flex flex-col shadow-lg">
                <div className="overflow-auto flex-1">
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                    >
                        <table className="w-full text-left border-collapse">
                            <thead className="sticky top-0 z-10 bg-black/20 backdrop-blur-md">
                                <tr>
                                    {/* Fixed Actions Column */}
                                    <th className="px-4 py-3 text-xs font-bold text-white/90 border-b border-r border-white/30 w-[80px] sticky left-0 z-20 backdrop-blur-md bg-black/20">İşlemler</th>

                                    <SortableContext
                                        items={columns.filter(c => c.visible).map(c => c.id)}
                                        strategy={horizontalListSortingStrategy}
                                    >
                                        {columns.map(col => (
                                            <SortableHeader
                                                key={col.id}
                                                column={col}
                                                sortConfig={sortConfig}
                                                onSort={handleSort}
                                            />
                                        ))}
                                    </SortableContext>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan={columns.length + 1} className="p-xl text-center">Yükleniyor...</td></tr>
                                ) : !hasSearched ? (
                                    <tr><td colSpan={columns.length + 1} className="p-xl text-center text-secondary">Arama yapmak için kriterleri belirleyip 'ARA' butonuna basınız.</td></tr>
                                ) : filteredData.length === 0 ? (
                                    <tr><td colSpan={columns.length + 1} className="p-xl text-center text-secondary">Kayıt bulunamadı.</td></tr>
                                ) : (
                                    filteredData.map(row => (
                                        <tr key={row.id} className="border-b border-white/5 hover:bg-white/5 transition-colors group">
                                            <td className="px-4 py-2 sticky left-0 group-hover:bg-white/5 transition-colors z-10 box-border border-b border-r border-white/10 backdrop-blur-md" style={{ backgroundColor: 'var(--bg-card)' }}>
                                                <Button
                                                    size="sm"
                                                    variant="primary"
                                                    onClick={() => handleEdit(row)}
                                                    style={{
                                                        fontSize: '0.7rem',
                                                        padding: '4px 10px',
                                                        borderRadius: '8px',
                                                        backgroundColor: 'var(--primary-color)',
                                                        color: '#fff',
                                                        border: 'none'
                                                    }}
                                                >
                                                    Düzelt
                                                </Button>
                                            </td>
                                            {columns.map(col => {
                                                if (!col.visible) return null;
                                                return (
                                                    <td key={col.id} className="px-4 py-2 text-xs whitespace-nowrap border-r border-white/10 last:border-r-0 text-left" style={{ color: 'var(--text-secondary)' }}>
                                                        {col.id === 'docNo' && <span className="font-mono text-primary">{row.reservationNumber || row.documentNumber || row.docNo || row.id}</span>}
                                                        {col.id === 'transporter' && (
                                                            <span
                                                                className="font-medium block truncate max-w-[150px]"
                                                                style={{ color: 'var(--text-primary)' }}
                                                                title={row.transporterName}
                                                            >
                                                                {(transporterMap[row.transporterId] || transporterMap[row.transporterName])?.shortName || row.transporterName}
                                                            </span>
                                                        )}
                                                        {col.id === 'plate' && <span className="font-mono" style={{ color: 'var(--text-primary)' }}>{row.plateNumber}</span>}
                                                        {col.id === 'vehicleType' && (
                                                            <span style={{ color: 'var(--text-secondary)' }}>
                                                                {row.vehicleTypeName ||
                                                                    row.metrics?.vehicleTypeName ||
                                                                    vehicleTypes.find(vt => vt.id === (row.vehicleTypeId || row.metrics?.vehicleType || row.vehicleType))?.name ||
                                                                    '-'}
                                                            </span>
                                                        )}
                                                        {col.id === 'transportType' && <span style={{ color: 'var(--text-secondary)' }}>{row.transportType || '-'}</span>}
                                                        {col.id === 'status' && (
                                                            <span className="font-medium" style={{ color: STATUS_COLORS[row.status] || '#9ca3af' }}>
                                                                {STATUS_LABELS[row.status] || row.status}
                                                            </span>
                                                        )}
                                                        {col.id === 'delayed' && (() => {
                                                            const planned = row.startTime instanceof Date ? row.startTime : new Date(row.startTime);
                                                            const actual = row.entryTime instanceof Date ? row.entryTime : (row.entryTime ? new Date(row.entryTime) : null);
                                                            const now = new Date();
                                                            let delayed = false;
                                                            if (actual) {
                                                                delayed = (actual - planned) / 60000 > 15;
                                                            } else if (!['cancelled', 'rejected', 'completed', 'facility_exited'].includes(row.status)) {
                                                                delayed = (now - planned) / 60000 > 15;
                                                            }
                                                            return delayed ? (
                                                                <span className="text-[10px] bg-red-500/20 text-red-400 px-2 py-0.5 rounded-full border border-red-500/30 w-fit font-bold uppercase tracking-wider">GECİKTİ</span>
                                                            ) : (
                                                                <span className="text-[10px] text-secondary opacity-30 items-center justify-center flex">-</span>
                                                            );
                                                        })()}
                                                        {col.id === 'volume' && (
                                                            <span>
                                                                {row.metrics?.volume || row.plannedVolume || 0} {volumeUnit}
                                                            </span>
                                                        )}
                                                        {col.id === 'weight' && (
                                                            <span>
                                                                {row.metrics?.weight || row.plannedWeight || 0} {weightUnit}
                                                            </span>
                                                        )}
                                                        {col.id === 'creator' && (
                                                            <div className="flex flex-col">
                                                                <span className="font-medium" style={{ color: 'var(--text-primary)' }}>{row.creatorName || '-'}</span>
                                                                {row.creatorRole && <span className="text-[10px] opacity-50 uppercase">{row.creatorRole}</span>}
                                                            </div>
                                                        )}
                                                        {col.id === 'date' && (row.startTime ? dayjs(row.startTime).format('DD.MM.YYYY') : (row.bookingDate || row.date || '-'))}
                                                        {col.id === 'time' && (
                                                            <span className="font-mono">
                                                                {typeof row.startTime === 'string' ? row.startTime : dayjs(row.startTime).format('HH:mm')} -
                                                                {typeof row.endTime === 'string' ? row.endTime : dayjs(row.endTime).format('HH:mm')}
                                                            </span>
                                                        )}
                                                        {col.id === 'dock' && (docks.find(d => d.id === row.dockId)?.name || 'Atanmadı')}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </DndContext>
                </div>
                <div className="p-sm border-t border-border flex justify-between items-center text-xs text-secondary font-medium" style={{ background: 'var(--bg-secondary)' }}>
                    <div className="flex gap-md">
                        <span>TOPLAM HACİM: <strong className="text-primary">{totals.volume.toLocaleString('tr-TR')} {volumeUnit}</strong></span>
                        <span>TOPLAM AĞIRLIK: <strong className="text-primary">{totals.weight.toLocaleString('tr-TR')} {weightUnit}</strong></span>
                    </div>
                    <div>
                        Toplam {filteredData.length} Kayıt
                    </div>
                </div>
            </div>

            {/* Edit Modal */}
            <ReservationModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSave={handleSaveReservation}
                initialData={selectedReservation}
                docks={docks}
                tenantId={user?.tenantId}
            />
        </div >
    );
};

export default ReservationTracking;
